import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, ArrowLeft, Calendar, Eye, Loader2 } from "lucide-react";

export default function BlogPost() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    supabase
      .from("blog_posts")
      .select("*")
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle()
      .then(({ data }) => {
        setPost(data);
        setLoading(false);
        // Increment views (fire and forget)
        if (data) {
          supabase.from("blog_posts").update({ views: (data.views || 0) + 1 }).eq("id", data.id).then(() => {});
        }
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Post não encontrado.</p>
        <Button onClick={() => navigate("/blog")}>Voltar ao Blog</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="fixed top-0 inset-x-0 z-50 glass-card border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <MessageSquare className="w-7 h-7 text-primary" />
            <span className="text-xl font-bold font-headline tracking-tight text-primary">SYRA</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/blog")}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Blog
          </Button>
        </div>
      </nav>

      <article className="pt-28 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          {post.cover_image_url && (
            <div className="aspect-video overflow-hidden rounded-xl mb-8">
              <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4">
            <Calendar className="h-4 w-4" />
            {post.published_at ? new Date(post.published_at).toLocaleDateString("pt-BR") : ""}
            <span>•</span>
            <span>{post.author_name}</span>
            <span>•</span>
            <Eye className="h-4 w-4" />
            <span>{post.views} visualizações</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold font-headline mb-4">{post.title}</h1>

          <div className="flex flex-wrap gap-2 mb-8">
            {(Array.isArray(post.tags) ? post.tags : []).map((tag: string, i: number) => (
              <Badge key={i} variant="outline">{tag}</Badge>
            ))}
          </div>

          <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line text-foreground/90 leading-relaxed">
            {post.content}
          </div>
        </div>
      </article>
    </div>
  );
}
